import { Card, Text, Icon, Colors } from '@amelio/ui-library';

import type { User } from '@/types/directory/User';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { QuestionTextRenderer } from '@/components/QuestionTextRenderer/QuestionTextRenderer';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { getTextByLanguage } from '@/utils/localization';

interface SelectedQuestionBannerProps {
    question: ReviewQuestion | null;
    variant?: 'default' | 'competency';
    previewEvaluee?: User;
}

export const SelectedQuestionBanner = ({
    question,
    variant = 'default',
    previewEvaluee,
}: SelectedQuestionBannerProps) => {
    const companyLanguage = useSelectedAppLanguage();
    const replaceDynamicTags = useReplaceDynamicTags();

    if (question === null) {
        return null;
    }

    const isCompetency = variant === 'competency';

    const cardClassName = isCompetency
        ? 'bg-primary-50 gradient-border-primary w-full pl-10'
        : 'w-full bg-white! pl-10';

    const iconColor = isCompetency ? Colors.BASE_600 : Colors.BASE_300;

    return (
        <div className={'relative w-full'}>
            {/* Icon with square background that overlaps the card */}
            <div
                className={
                    'absolute top-1/2 -left-3 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md bg-white shadow-md'
                }
            >
                <Icon
                    name={isCompetency ? 'settings-menu' : 'edit-edit-2'}
                    color={iconColor}
                    size={'md'}
                />
            </div>

            {/* Question card */}
            <Card
                className={cardClassName}
                borderRadius={'xl'}
                padding={'md'}
                variant={isCompetency ? 'flat' : undefined}
                bgColor={isCompetency ? Colors.PRIMARY_200 : undefined}
            >
                <Text
                    weight={'medium'}
                    color={isCompetency ? undefined : Colors.BASE_300}
                >
                    <QuestionTextRenderer
                        text={replaceDynamicTags({
                            text: getTextByLanguage(question.label, companyLanguage),
                            employee: previewEvaluee,
                        })}
                    />
                </Text>
            </Card>
        </div>
    );
};
